import { PageInfo } from '@/types'
import { useEffect, useState } from 'react'
import { usePageInfo } from './usePageInfo'
import { TSearchable } from '@/components/base/combobox'
import { dedupe } from './dedupe'

interface Props<Item> {
  pageInfo: PageInfo | undefined
  querySelected: Item | undefined
  queryCollection: Item[] | undefined
  buildSearchable: (item: Item) => TSearchable
  alphabetical?: boolean
}

export const useCombobox = <Item>({
  pageInfo,
  querySelected,
  queryCollection,
  buildSearchable,
  alphabetical = true,
}: Props<Item>) => {
  const { pageInfo: pageInfoRef } = usePageInfo({ pageInfo })

  const [builderCallback] = useState(() => buildSearchable ?? (() => ({} as TSearchable)))
  const [selected, setSelected] = useState<TSearchable>()
  const [items, setItems] = useState<TSearchable[]>([])

  useEffect(() => {
    if (!querySelected) return

    setSelected(builderCallback(querySelected))
  }, [setSelected, querySelected, builderCallback])

  useEffect(() => {
    if (!queryCollection) return

    const builtItems = queryCollection.map(builderCallback)
    if (selected) builtItems.push(selected)

    if (alphabetical) {
      setItems(dedupe(builtItems).sort((a, b) => ('' + a.name).localeCompare(b.name)))
    } else {
      setItems(dedupe(builtItems))
    }
  }, [queryCollection, setItems, selected, alphabetical, builderCallback])

  return {
    pageInfo: pageInfoRef,
    items
  }
}
